import { useMutation } from '@apollo/client';
import { Button, COLOR, EmptyContent } from '@cosuno/cosuno-ui';
import React, { useState } from 'react';

import { PRICING_ADD_ON, PRICING_PLAN } from '~/__gql__/graphql';
import { toast } from '~/packages/Toast';
import { mutationRequestPricingUpsell, showUpsellSuccessToast } from '~/packages/Upsell';
import { AiTag } from '~/shared/components/AiTag';
import useTranslation from '~/shared/hooks/useTranslation';

import { PricingItemCard } from '../PricingItemCard';
import { CardsWrapper, StyledEmptyContent } from './Styles';

export const PACKAGES_CONSULTATION_EMAIL = 'packages@cosuno.de';

interface PricingPlansContentProps {
  /** The highlighted plan will be made to stand out visually and will be
   * recommended to users. */
  highlightedPlan?: PRICING_PLAN | null;
  /** Will be called when a user clicks on "Request more information" for one
   * of the plans or add-ons. */
  onRequestItem?: (item: PRICING_PLAN | PRICING_ADD_ON) => void;
  /** Optional render function for the plans heading */
  renderPlansHeading?: () => React.ReactNode;
  /** Optional render function for the add-ons heading */
  renderAddOnsHeading?: () => React.ReactNode;
}

const PricingPlansContent: React.FC<PricingPlansContentProps> = ({
  highlightedPlan = null,
  onRequestItem,
  renderPlansHeading,
  renderAddOnsHeading,
}) => {
  const { t } = useTranslation('company');

  const [requestingPlan, setRequestingPlan] = useState<PRICING_PLAN | null>(null);
  const [requestingAddOn, setRequestingAddOn] = useState<PRICING_ADD_ON | null>(null);
  const [requestPricingUpsell, { loading: isRequesting }] = useMutation(
    mutationRequestPricingUpsell,
  );

  const plans = [
    { plan: PRICING_PLAN.silver, color: COLOR.textTertiary },
    { plan: PRICING_PLAN.gold, color: COLOR.warning },
    { plan: PRICING_PLAN.enterprise, color: COLOR.highlightNeon },
  ] as const;

  const addOns = [
    { addOn: PRICING_ADD_ON.analytics, color: COLOR.highlightBrand, tag: undefined },
    { addOn: PRICING_ADD_ON.ai, color: COLOR.highlightNeon, tag: <AiTag /> },
  ] as const;

  return (
    <>
      {renderPlansHeading && renderPlansHeading()}
      <CardsWrapper>
        {plans.map(({ plan, color }) => (
          <PricingItemCard
            key={plan}
            item={plan}
            color={color}
            isHighlighted={plan === highlightedPlan}
            highlightTagText={t('pricingPlans.highlightTag')}
            packageTitle={t(`pricingPlans.packages.${plan}.packageTitle`)}
            packageDescription={t(`pricingPlans.packages.${plan}.packageDescription`)}
            featuresTitle={t(`pricingPlans.packages.${plan}.featuresTitle`)}
            featuresItems={Array.from(
              t(`pricingPlans.packages.${plan}.featuresItems`, { returnObjects: true }),
            )}
            actionButton={
              <Button
                data-cy-button-name={`pricing-plan-${plan}`}
                onClick={async () => {
                  onRequestItem?.(plan);

                  try {
                    setRequestingPlan(plan);

                    await requestPricingUpsell({
                      variables: { pricingPlan: plan },
                    });

                    showUpsellSuccessToast(t);
                  } catch (error) {
                    toast.error(error);
                  } finally {
                    setRequestingPlan(null);
                  }
                }}
                working={isRequesting && requestingPlan === plan}
              >
                {t('pricingPlans.requestMoreInfoButton')}
              </Button>
            }
          />
        ))}
      </CardsWrapper>
      {renderAddOnsHeading && renderAddOnsHeading()}
      <CardsWrapper>
        {addOns.map(({ addOn, color, tag }) => (
          <PricingItemCard
            key={addOn}
            item={addOn}
            color={color}
            isHighlighted={false}
            highlightTagText={t('pricingPlans.highlightTag')}
            packageTitle={t(`pricingPlans.addOns.${addOn}.addOnTitle`)}
            packageDescription={t(`pricingPlans.addOns.${addOn}.addOnDescription`)}
            packageTag={tag}
            featuresItems={Array.from(
              t(`pricingPlans.addOns.${addOn}.featuresItems`, { returnObjects: true }),
            )}
            actionButton={
              <Button
                data-cy-button-name={`pricing-add-on-${addOn}`}
                onClick={async () => {
                  onRequestItem?.(addOn);

                  try {
                    setRequestingAddOn(addOn);

                    await requestPricingUpsell({
                      variables: { pricingAddOn: addOn },
                    });

                    showUpsellSuccessToast(t);
                  } catch (error) {
                    toast.error(error);
                  } finally {
                    setRequestingAddOn(null);
                  }
                }}
                working={isRequesting && requestingAddOn === addOn}
              >
                {t('pricingPlans.requestMoreInfoButton')}
              </Button>
            }
          />
        ))}
      </CardsWrapper>
      <StyledEmptyContent>
        <EmptyContent.CallToAction>{t('pricingPlans.undecidedPlan.cta')}</EmptyContent.CallToAction>
        <EmptyContent.Explanation>
          {t('pricingPlans.undecidedPlan.explanation')}
        </EmptyContent.Explanation>
        <Button
          data-cy-button-name="consultationEmail"
          iconLeft={{ type: 'email' }}
          variant="secondaryBrand"
          linkToExternal={`mailto:${PACKAGES_CONSULTATION_EMAIL}`}
        >
          {PACKAGES_CONSULTATION_EMAIL}
        </Button>
      </StyledEmptyContent>
    </>
  );
};

export default PricingPlansContent;
